import { h } from 'preact';
import Store from 'store/store';

import * as jsonPointer from 'utils/jsonPointer';
import { confirm } from 'utils/modal';
import { parseDate } from 'utils/utils';
import formatDateTime from 'utils/formatDateTime';
import { canUser } from 'utils/roles';
import { getPageName } from 'utils/thundr';

import HubPageEditor from 'components/editor/pages/hubPageEditor';
import Button from 'components/button/button';
import HubNavigation from 'components/editor/hubNavigation/hubNavigation';
import HubFooter from 'components/editor/hubFooter/hubFooter';
import SelectableSection from 'components/editor/selectableSection';
import SimpleField from 'components/editor/settings/simpleField';
import DateField from 'components/editor/settings/dateField';

import s from 'components/editor/pages/news.sss';

export default class NewsEditor extends HubPageEditor {
	constructor(props) {
		super(props);

		this.addArticle = this.addArticle.bind(this);
		this.onDeleteArticleClick = this.onDeleteArticleClick.bind(this);
	}

	// TODO: remove old articles on mount? Or when anything is changed?

	get articles() {
		const content = this.props.content;
		const limit = new Date();
		limit.setMonth(limit.getMonth() - 3);
		const limitStr = formatDateTime(limit, 'yyyy-MM-dd');
		return Object.entries(content?.articles || {}).filter(([, article]) => !article.date || article.date >= limitStr);
	}

	addArticle() {
		const id = crypto.randomUUID();
		jsonPointer.get(Store.get().data, `${this.props.dataRoot || ''}/content/articles`).set(id, {});
		this.props.select(`/articles/${id}`);
	}

	sortArticles(articles) {
		return articles.sort((a, b) => {
			if (!a[1].date && !b[1].date) return 0;
			if (!a[1].date) return -1;
			if (!b[1].date) return 1;
			return b[1].date.localeCompare(a[1].date);
		});
	}

	async deleteArticle() {
		if (!this.props.activeElement || !jsonPointer.has(this.props.content, this.props.activeElement)) {
			return;
		}
		const path = jsonPointer.parse(`${this.props.dataRoot || ''}/content${this.props.activeElement}`);
		const key = path.pop();
		const parent = jsonPointer.get(Store.get().data, path);
		parent.remove(key);
		this.selectParentLevel();
	}

	async onDeleteArticleClick() {
		const ok = await confirm('This will delete the selected news item and cannot be undone', { title: 'Delete news item?', danger: true, button: 'Delete item' });
		if (!ok) return;
		this.deleteArticle();
	}

	renderVisual() {
		const articlesPerMonth = Object.groupBy(this.articles, ([, article]) => article.date?.slice(0, 7) || undefined);
		const months = Object.keys(articlesPerMonth).sort().reverse();
		const pageName = this.getPageName('news', 'News');
		return (
			<div class={s.visual}>
				<HubNavigation />
				<div class={s.pageHeader}>
					<div class={s.breadcrumbs}>{this.getPageName('landing', 'Home')} / <span class={s.active}>{pageName}</span></div>
					<div class={s.pageTitle} onClick={() => this.props.select(`/articles`)}>{pageName}</div>
				</div>
				<SelectableSection section="/articles" class={s.months} {...this.props}>
					{months.map(month => (
						<div class={s.month}>
							{month && month !== 'undefined' && <div class={s.monthName}>{formatDateTime(parseDate(month), 'MMMM', this.getCopy('$DATETIME_LOCALE'))}</div>}
							<div class={s.articles}>
								{this.sortArticles(articlesPerMonth[month]).map(([id, article]) => (
									<SelectableSection section={`/articles/${id}`} class={s.article} {...this.props}>
										<div class={s.articleTitle}>{article.title}</div>
										<div class={s.dateAndSource}>
											<div class={s.articleDate}>{formatDateTime(parseDate(article.date), this.getCopy('$NEWS_DATE_FORMAT', 'MM.dd.yyyy'))}</div>
											<div class={s.articleSource}>{article.source}</div>
										</div>
									</SelectableSection>
								))}
							</div>
						</div>
					))}
				</SelectableSection>
				<HubFooter />
			</div>
		);
	}

	renderArticlesEditor() {
		const articles = this.sortArticles(this.articles);
		return (
			<div class={s.articles} key="articles">
				<div class={s.note}>
					Articles older than 3 months will be automatically removed.
				</div>
				<Button primary icon="plus" class={s.add} onClick={this.addArticle} disabled={!canUser('write')}>Add news entry</Button>
				<div class={s.list}>
					{articles.map(([id, article]) => (
						<div class={s.item} key={id}>
							<span>{article.title}</span>
							<button class={s.edit} onClick={() => this.props.select(`/articles/${id}`)}>Edit</button>
						</div>
					))}
				</div>
			</div>
		);
	}

	renderArticleEditor() {
		if (!this.props.activeElement || !jsonPointer.has(this.props.content, this.props.activeElement)) {
			return;
		}
		let basePath = `${this.props.dataRoot || ''}/content${this.props.activeElement}`;
		// TODO: try to extract site name & date from url if not set yet
		// meta[property="article:published"] / article:published_time
		// meta[property="og:site_name"]
		// (note: will have to be server-side due to CORS)

		const today = new Date();
		const todayStr = formatDateTime(today, 'yyyy-MM-dd');

		return (
			<div class={s.article} key={basePath}>
				<SimpleField dark label="Link" path={`${basePath}/url`} />
				<SimpleField dark label="News item title" path={`${basePath}/title`} />
				<DateField dark label="Date" path={`${basePath}/date`} max={todayStr} />
				<SimpleField dark label="Source website" path={`${basePath}/source`} />
				<Button danger onClick={this.onDeleteArticleClick} disabled={!canUser('write')}>Delete item</Button>
			</div>
		);
	}

	renderSettings() {
		let content;
		const nav = [{ label: 'News', path: '/articles' }];
		const activeElement = this.props.activeElement;
		if (activeElement === '/articles') {
			content = this.renderArticlesEditor();
		} else if (activeElement?.startsWith('/articles/')) {
			nav.push({ label: 'News entry', path: activeElement });
			content = this.renderArticleEditor();
		}
		return (
			<div class={s.settings}>
				{this.renderSettingsNavigation(nav)}
				{content}
			</div>
		);
	}
}